import { defineStore } from 'pinia'
import { useResourcesApi } from '~/services/apis/resources'
import type { SelectOptionContext } from '~/types'

export * from './user'
export * from './products'
export * from './loader'
export * from './notifications-activities'

export const useStore = defineStore('store', {
	state: () => ({
		notificationMessage: '',
		notificationStatus: 0,
		notificationModal: false,
		states: <string[]>[],
		loadingDashboard: false,
		loaderText: '',
		todoRecords: [] as NotificationTodo[],
		insuranceCategory: [
			{
				label: 'Private',
				value: 'Private'
			},
			{
				label: 'Commercial',
				value: 'Commercial'
			}
		] as SelectOptionContext,
		allTransactions: {} as TransactionsResponse,
		'terms-and-condition': [
			'This is a subscription based plan which  means that you will not be charged until the end of your cover period.',
			'This plan will automatically renew when it expires.',
			'If a staff member decides to leave your organisation, you can easily replace them with a new staff before the start of the next month. Keep in mind that any changes you make will only go into effect at the beginning of the following month.'
		],
		colors: [] as SelectOptionContext,
		documentTypes: [] as SelectOptionContext,
		claimsGroup: {
			travel: {},
			health: {
				incident_types: [
					{
						label: 'Virtual consultation',
						value: 'Virtual consultation'
					},
					{
						label: 'Hospital visit',
						value: 'Hospital visit'
					}
				]
			},
			auto: {
				incident_types: [
					{
						label: 'Stolen vehicle',
						value: 'Stolen vehicle'
					},
					{
						label: 'Accidental damage',
						value: 'Accidental damage'
					},
					{
						label: 'Loss/ damage of personal effect',
						value: 'Loss/ damage of personal effect'
					}
				]
			},
			gadget: {
				incident_types: [
					{
						label: 'Screen damage',
						value: 'Screen damage'
					},
					{
						label: 'Liquid damage',
						value: 'Liquid damage'
					},
					{
						label: 'Theft',
						value: 'Theft'
					},
					{
						label: 'Burglary',
						value: 'Burglary'
					}
				]
			}
		},
		claimColors: {
			medical_expenses: ''
		},
		employeesTableColumn: [
			{
				title: 'S/N',
				dataIndex: 'sn',
				key: 'sn'
			},
			{
				title: 'Name',
				dataIndex: 'name',
				key: 'name',
				class: 'min-w-[200px]'
			},
			{
				title: 'Email Address',
				dataIndex: 'email',
				key: 'email'
			},
			{
				title: 'Phone Number',
				dataIndex: 'phone_number',
				key: 'phone_number',
				className: 'whitespace-nowrap'
			},
			{
				title: 'Date enrolled',
				dataIndex: 'enrolled_date',
				key: 'enrolled_date',
				align: 'center',
				class: 'min-w-[150px]'
			},
			{
				title: 'Total policies',
				dataIndex: 'total_plans',
				key: 'total_plans',
				className: 'whitespace-nowrap',
				class: 'min-w-[150px]'
			},

			{
				title: 'Status',
				dataIndex: 'status',
				key: 'status'
			},
			{
				title: 'Details',
				dataIndex: 'details',
				key: 'details',
				className: 'min-w-[100px]'
			}
		] as TableColumn,
		marketingChannels: [
			'twitter',
			'instagram',
			'google',
			'facebook',
			'website',
			'bdm',
			'sales_team',
			'sales_agent',
			'techcabal',
			'technext',
			'naira metrics',
			'chowdeck',
			'businessday',
			'punch',
			'jennifer',
			'jumoke',
			'angela',
			'alma',
			'dochase',
			'dochmpdgen'
		],
		pendingIdsForMigration: [] as (string | number)[],
		healthPlanTypes: [
			{
				value: 'standard',
				label: 'Standard',
				content: 'For staff with/ without dependants',
				icon: {
					dir: 'other',
					name: 'avatar'
				}
			},
			{
				value: 'family',
				label: 'Family',
				content: 'For staff with family dependants',
				icon: {
					dir: 'other',
					name: 'family'
				}
			}
		],
		referralChannels: [
			{
				label: 'Billboard',
				value: 'billboard'
			},
			{
				label: 'Facebook',
				value: 'facebook'
			},
			{
				label: 'Instagram',
				value: 'instagram'
			},
			{
				label: 'Twitter (X)',
				value: 'twitter'
			},
			{
				label: 'LinkedIn',
				value: 'linkedIn'
			},
			{
				label: 'Radio',
				value: 'radio'
			},
			{
				label: 'Newsletter',
				value: 'newsletter'
			},
			{
				label: 'Referral',
				value: 'referral'
			},
			{
				label: 'Influencer',
				value: 'influencer'
			},
			{
				label: 'Other',
				value: 'other'
			}
		] as SelectOptionContext,
		hospitalTypes: [
			{ label: 'All', value: '', icon: 'all' },
			{ label: 'Eye care', value: 'ophthalmology', icon: 'eye-care' },
			{ label: 'Hospital', value: 'hospital', icon: 'hospital' },
			{ label: 'Dental', value: 'dental', icon: 'dental' },
			{ label: 'Clinic', value: 'laboratory centres', icon: 'clinic' }
		],
		hospitalTypeIcon: {
			ophthalmology: 'eye-care',
			hospital: 'hospital',
			dental: 'dental',
			'laboratory centres': 'clinic',
			'diagnostic centres': 'clinic'
		} as Record<string, string>,
		businessTypes: [
			{
				label: 'Sole Proprietorship (BN)',
				value: 'sole proprietorship',
				initial: 'BN'
			},
			{
				label: 'Limited Liability Company LTD (RC)',
				value: 'limited liability company',
				initial: 'RC'
			},
			{
				label: 'Non-Governmental Organization (IT)',
				value: 'non-governmental organization',
				initial: 'IT'
			},
			{
				label: 'Not registered with CAC',
				value: 'none',
				initial: ''
			}
		] as SelectOptionContext,
		companySupport: [] as CompanySupport[],
		maritalStatus: [
			{
				value: 'single',
				label: 'Single'
			},
			{
				value: 'married',
				label: 'Married'
			},
			{
				value: 'divorced',
				label: 'Divorced'
			},
			{
				value: 'widowed',
				label: 'Widowed'
			},
			{
				value: 'separated',
				label: 'Separated'
			}
		] as SelectOptionContext
	}),
	getters: {
		States(state) {
			return state.states.map(e => ({
				label: e,
				value: e.toLocaleLowerCase()
			}))
		},
		AllTransactions(state) {
			return state.allTransactions
		},
		Colors(state) {
			return state.colors
		},
		DocumentUploadTypes(state) {
			return state.documentTypes
		}
	},
	actions: {
		toggleModal(value: boolean) {
			const state = this.$state
			state.notificationModal = value
		},
		setTransactions(transactionResponse: TransactionsResponse) {
			this.allTransactions = transactionResponse
		},
		setColors(payload: SelectOptionContext) {
			this.colors = payload
		},
		setDocumentTypes(payload: SelectOptionContext) {
			this.documentTypes = payload
		},
		setTodoRecords(payload: NotificationTodo[]) {
			this.todoRecords = payload
		},
		getHospitalType(arg: string) {
			return this.hospitalTypes.find(category => category.value == arg)
		},
		/**
		 * Retrieves all support contact objects matching a specific type.
		 * Use this when you need to iterate over multiple support options (e.g., WhatsApp list).
		 */
		getSupportGroup(type: string): CompanySupport[] {
			const items = this.companySupport
			if (!items) return []
			return items.filter(item => item.type === type)
		},

		/**
		 * Retrieves a single support contact matching a type.
		 * Optionally matches a specific provider name/identifier (e.g., 'hygeia').
		 * Falls back to the first match if no provider is specified.
		 */
		getSupportContact(type: string, provider?: string | number): CompanySupport | null {
			const items = this.companySupport
			if (!items) return null

			const matches = items.filter(item => item.type === type)
			if (matches.length === 0) return null

			if (typeof provider === 'number') {
				return matches[provider] || null
			}

			if (provider) {
				return (
					items.find(
						item =>
							item.type === type &&
							(item.health_line === provider || item.line === provider || item.type === provider)
					) ||
					items.find(item => item.type === provider) ||
					null
				)
			}

			return matches[0] || null
		},
		async fetchCompanySupport() {
			const { getCompanyInfo } = useResourcesApi()
			const { data } = await getCompanyInfo()
			if (Array.isArray(data)) {
				this.companySupport = data
			}
		}
	}
})
